/**
 * Home - Main dashboard page after login
 *
 * Redesigned to mirror the Picasso dashboard layout
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { Sidebar } from '../components/layout';

interface BoardSummary {
  id: string;
  name: string;
  description?: string;
  createdAt: number;
  updatedAt: number;
  pinnedAt?: number;
  thumbnailUrl?: string;
}

export function Home() {
  const navigate = useNavigate();
  const { user, isLoading } = useCurrentUser();
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [isLoadingBoards, setIsLoadingBoards] = useState(true);
  const [boardsFilter, setBoardsFilter] = useState<'recent' | 'unshared'>('recent');
  const [boardsView, setBoardsView] = useState<'grid' | 'table'>('grid');

  // Redirect to signin if not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/signin');
    }
  }, [isLoading, user, navigate]);

  const fetchBoards = useCallback(async () => {
    setIsLoadingBoards(true);
    try {
      const response = await fetch('/api/boards', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setBoards(data?.data?.boards || []);
      }
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    } finally {
      setIsLoadingBoards(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchBoards();
  }, [user, fetchBoards]);

  const createBoard = useCallback(async () => {
    try {
      const response = await fetch('/api/boards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: 'Untitled Board',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const boardId = data?.data?.id;
        if (boardId) {
          navigate(`/boards/${boardId}`);
        }
      }
    } catch (error) {
      console.error('Failed to create board:', error);
    }
  }, [navigate]);

  const formatRelativeTime = (timestamp: number) => {
    const now = new Date();
    const date = new Date(timestamp * 1000); // Assuming timestamp is seconds
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-color-text-secondary border-t-color-text" />
      </div>
    );
  }

  const sortedBoards = [...boards].sort((a, b) => b.updatedAt - a.updatedAt);
  // Simple filter logic for demo; 'unshared' logic would depend on real data properties
  const visibleBoards = boardsFilter === 'recent'
    ? sortedBoards.slice(0, 8)
    : sortedBoards;

  return (
    <div className="flex min-h-screen bg-color-neutral-2 font-inter text-color-main">
      <Sidebar />

      <main className="flex-1 ml-[240px]">
        {/* Launchpad Button */}
        <Link
          to="/ai/chat"
          target="_blank"
          className="fixed bottom-5 left-[23px] z-50 flex w-[147px] items-center justify-center gap-2 rounded-xl bg-gradient-vivo py-3 text-sm font-medium text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
          Open Launchpad
        </Link>

        {/* AppBar equivalent */}
        <header className="flex h-16 items-center justify-between border-b border-color-neutral-3 bg-white px-6">
          <div className="flex items-center gap-4">
            {/* Breadcrumbs or other header elements could go here */}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-color-vivo text-color-purple-dark text-sm font-bold shadow-sm ring-2 ring-white">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        <div className="p-8">
          {/* Header Section */}
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-color-main font-manrope">
              Companions
            </h1>

            <div className="flex items-center gap-3">
              {/* View Toggle */}
              <div className="flex items-center rounded-lg bg-color-neutral-2 p-1 border border-color-neutral-3">
                <button
                  type="button"
                  onClick={() => setBoardsView('grid')}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition-all ${boardsView === 'grid'
                      ? 'bg-white shadow-sm text-color-main'
                      : 'text-color-dark-2 hover:bg-color-neutral-3'
                    }`}
                  title="Grid view"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setBoardsView('table')}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition-all ${boardsView === 'table'
                      ? 'bg-white shadow-sm text-color-main'
                      : 'text-color-dark-2 hover:bg-color-neutral-3'
                    }`}
                  title="Table view"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6"></line>
                    <line x1="8" y1="12" x2="21" y2="12"></line>
                    <line x1="8" y1="18" x2="21" y2="18"></line>
                    <line x1="3" y1="6" x2="3.01" y2="6"></line>
                    <line x1="3" y1="12" x2="3.01" y2="12"></line>
                    <line x1="3" y1="18" x2="3.01" y2="18"></line>
                  </svg>
                </button>
              </div>

              {/* Create Button */}
              <button
                onClick={createBoard}
                className="flex items-center gap-2 rounded-lg bg-color-main text-white px-4 py-2 text-sm font-medium hover:bg-color-main-darker transition-colors shadow-button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Create
              </button>
            </div>
          </div>

          {/* Separator */}
          <div className="h-[2px] w-full bg-[#F8F8F8] mb-8" />

          {/* Content Section */}
          <div className="flex flex-col gap-6">
            {/* Tabs */}
            <div className="flex gap-6 border-b border-color-lightline">
              <button
                onClick={() => setBoardsFilter('recent')}
                className={`pb-3 text-sm font-medium transition-colors relative ${boardsFilter === 'recent'
                    ? 'text-color-main after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-color-main'
                    : 'text-color-blended hover:text-color-main'
                  }`}
              >
                Recent
              </button>
              <button
                onClick={() => setBoardsFilter('unshared')}
                className={`pb-3 text-sm font-medium transition-colors relative ${boardsFilter === 'unshared'
                    ? 'text-color-main after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-color-main'
                    : 'text-color-blended hover:text-color-main'
                  }`}
              >
                Unshared
              </button>
            </div>

            {/* Board List */}
            {isLoadingBoards ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="aspect-[4/3] rounded-xl bg-color-neutral-3 animate-pulse" />
                ))}
              </div>
            ) : visibleBoards.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-[18px] bg-white py-20 text-center shadow-card-light border border-color-neutral-3">
                <div className="text-xl font-medium text-color-main mb-2">No boards yet</div>
                <div className="text-sm text-color-blended">Create your first board to get started</div>
              </div>
            ) : boardsView === 'grid' ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {visibleBoards.map((board) => (
                  <button
                    key={board.id}
                    onClick={() => navigate(`/boards/${board.id}`)}
                    className="group flex flex-col overflow-hidden rounded-xl bg-white border border-color-neutral-3 transition-all hover:-translate-y-1 hover:shadow-card hover:border-color-dark-1 text-left"
                  >
                    <div className="aspect-[4/3] w-full bg-color-surface-muted flex items-center justify-center border-b border-color-lightline group-hover:bg-color-neutral-1 transition-colors">
                      {board.thumbnailUrl ? (
                        <img src={board.thumbnailUrl} alt={board.name} className="h-full w-full object-cover" />
                      ) : (
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="text-color-blended group-hover:text-color-dark-2 transition-colors">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="mb-1 truncate text-sm font-medium text-color-main group-hover:text-color-blue transition-colors">{board.name}</div>
                      <div className="text-xs text-color-blended">Edited {formatRelativeTime(board.updatedAt)}</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="w-full overflow-hidden rounded-xl border border-color-neutral-3 bg-white shadow-card-light">
                <table className="w-full text-left text-sm">
                  <thead className="bg-color-neutral-2 text-color-blended-darker font-medium">
                    <tr>
                      <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Last Edited</th>
                      <th className="px-6 py-3 font-medium text-xs uppercase tracking-wider">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-color-lightline text-color-main">
                    {visibleBoards.map((board) => (
                      <tr
                        key={board.id}
                        onClick={() => navigate(`/boards/${board.id}`)}
                        className="cursor-pointer hover:bg-color-neutral-1 transition-colors group"
                      >
                        <td className="px-6 py-4 font-medium group-hover:text-color-blue transition-colors">{board.name}</td>
                        <td className="px-6 py-4 text-color-dark-2">{formatRelativeTime(board.updatedAt)}</td>
                        <td className="px-6 py-4 text-color-dark-2">{formatRelativeTime(board.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
